import React from "react";
import { ScrollShadow, cn } from "@heroui/react";

interface ScrollingBannerProps extends Omit<React.ComponentProps<typeof ScrollShadow>, "children"> {
  children?: React.ReactNode;
  isReverse?: boolean;
  showShadow?: boolean;
  shouldPauseOnHover?: boolean;
  isVertical?: boolean;
  gap?: string;
  duration?: number;
}

const ScrollingBanner = React.forwardRef<HTMLDivElement, ScrollingBannerProps>(
  (
    {
      className,
      isReverse,
      isVertical = false,
      gap = "1rem",
      showShadow = true,
      shouldPauseOnHover = true,
      duration = 40,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const childArray = React.Children.toArray(children);

    const styleWithVars: React.CSSProperties = {
      ...(style as React.CSSProperties),
      "--gap": gap,
      "--duration": `${duration}s`,
    } as React.CSSProperties;

    return (
      <ScrollShadow
        {...props}
        ref={ref}
        isEnabled={showShadow}
        offset={-20}
        size={300}
        orientation={isVertical ? "vertical" : "horizontal"}
        visibility="both"
        className={cn(
          "flex",
          {
            "w-full": !isVertical,
            "overflow-y-hidden": isVertical,
            "overflow-x-hidden": !isVertical,
            "max-h-[calc(100vh-200px)]": isVertical,
          },
          className
        )}
        style={styleWithVars}
      >
        <div
          className={cn(
            "flex w-max items-stretch gap-[var(--gap)]",
            {
              "flex-col": isVertical,
              "h-full": isVertical,
              "animate-scrolling-banner": !isVertical,
              "animate-scrolling-banner-vertical": isVertical,
              "[animation-direction:reverse]": isReverse,
              "hover:[animation-play-state:paused]": shouldPauseOnHover,
            }
          )}
        >
          {childArray}
          {childArray}
        </div>
      </ScrollShadow>
    );
  }
);

ScrollingBanner.displayName = "ScrollingBanner";

export default ScrollingBanner;
